import { UploadFileOutlined } from "@mui/icons-material";
import { Alert, Button, Stack, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import Modal, { CommonModalProps } from "..";
import { assetService, cacheService, mediaService } from "../../../Core/Api/services";
import {
  TitleCoverLocation,
  TitleCoverResource,
  TitlePosterLocation,
  TitlePosterResource,
} from "../../../Core/Api/Types/asset-resources";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { UploadCardFormatDto } from "../../../Core/Api/Types/db-format-infos";
import { FileLinkedType } from "../../../Core/Api/Types/file-linked-type";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { GetMetadataByTagName } from "../../../Core/Utils/asset-object-utils";
import { GetObjectMediaFiles, SetFetchedMediaFiles } from "../../../Redux/Reducers/redux-slice-asset";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import PosterOrCoverMediaCard, { TitlePosterOrCoverMediaProps } from "../../Cards/MediaCard/poster-or-cover-media-card";
import UploadCard, { UploadContext } from "../../Cards/UploadCard/upload-card";

export type ModalTitleSetPosterOrCoverElementProps = {
  mode: "poster" | "cover";
  titleId: number;
  format: UploadCardFormatDto | undefined;
  title: DbAssetObjectInfos;
  currentResource: TitlePosterResource | TitleCoverResource | null;
  onSuccess: () => void;
};

export type ModalTitleSetPosterOrCoverProps = CommonModalProps<ModalTitleSetPosterOrCoverElementProps>;

const ModalTitleSetPosterOrCover = React.forwardRef<any, ModalTitleSetPosterOrCoverProps>((props, ref) => {
  const { mode, titleId, format, title, currentResource, onSuccess, closeModal } = props;
  const titleMediaFiles = useAppSelector(GetObjectMediaFiles); // [LB] Note: As we are on title page, media files are already loaded
  const enqueueSnackbar = useCustomSnackbar();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = React.useState(false);
  const [selectedMedia, setSelectedMedia] = React.useState<TitlePosterOrCoverMediaProps | null>(null);
  const [uploadedFileIds, setUploadedFileIds] = React.useState<number[]>([]);

  // Store used metadatas for GetMetadataByTagName to be faster
  const usedMetas = React.useMemo(() => {
    return cacheService
      .GetMetadata()
      .filter((x) => x.tagName === "contentType" || x.tagName === "techWidth" || x.tagName === "techHeight");
  }, []);

  const artworks: TitlePosterOrCoverMediaProps[] = React.useMemo(() => {
    if (!usedMetas) return [];

    // Get Artwork content
    const content: DbAssetObjectInfos | undefined = title.childObjects?.find(
      (x) => GetMetadataByTagName(x, "contentType", usedMetas)?.value === "Artwork"
    );
    if (!content || !content.childObjects) return [];

    // [LB] Note: we will use width and height of artworks to determine if it's a poster or a cover. Squares will be used in both cases.
    const widthAndSizeFilter = (asset: DbAssetObjectInfos): boolean => {
      const width = GetMetadataByTagName(asset, "techWidth", usedMetas)?.value;
      const height = GetMetadataByTagName(asset, "techHeight", usedMetas)?.value;

      if (!width || !height) return false;

      if (mode === "poster") return height >= width;
      else return width >= height;
    };

    // Get all image assets that match the filter above, and return them as TitlePosterOrCoverMediaProps
    return content.childObjects
      .filter((x) => !x.isDeleted && x.type === ObjectType.Image && widthAndSizeFilter(x))
      .map((asset) => ({
        kind: "artwork",
        id: asset.id,
        width: GetMetadataByTagName(asset, "techWidth", usedMetas)?.value || 0,
        height: GetMetadataByTagName(asset, "techHeight", usedMetas)?.value || 0,
        name: asset.name,
      }));
  }, [title, usedMetas]);

  const mediaFiles: TitlePosterOrCoverMediaProps[] = React.useMemo(() => {
    if (!titleMediaFiles || !titleMediaFiles.length) return [];

    const linkedType = mode === "poster" ? FileLinkedType.PosterTitleMain : FileLinkedType.CoverTitleMain;

    return titleMediaFiles
      .filter((x) => x.linkedType === linkedType && x.properties)
      .map((file) => ({
        kind: "file",
        id: file.id,
        width: file.properties.size?.width || 0,
        height: file.properties.size?.height || 0,
        name: file.name || "",
      }));
  }, [titleMediaFiles]);

  const mediaCardsData: TitlePosterOrCoverMediaProps[] = React.useMemo(() => {
    return [...artworks.sort((a, b) => b.id - a.id), ...mediaFiles.sort((a, b) => b.id - a.id)];
  }, [artworks, mediaFiles]);

  const currentMedia: TitlePosterOrCoverMediaProps | null = React.useMemo(() => {
    if (!currentResource) return null;

    if (
      currentResource.location === TitlePosterLocation.Artwork ||
      currentResource.location === TitleCoverLocation.Artwork
    ) {
      const artwork = artworks.find((x) => x.id === currentResource.resourceId);
      if (artwork) return artwork;
    } else {
      const file = mediaFiles.find((x) => x.id === currentResource.resourceId);
      if (file) return file;
    }

    return null;
  }, [currentResource, artworks, mediaFiles]);

  const isMediaSelected = (media: TitlePosterOrCoverMediaProps): boolean => {
    if (!selectedMedia) return false;
    return selectedMedia.id === media.id && selectedMedia.kind === media.kind;
  };

  const handleSelectMedia = (media: TitlePosterOrCoverMediaProps) => {
    if (selectedMedia && selectedMedia.id === media.id && selectedMedia.kind === media.kind) setSelectedMedia(null);
    else setSelectedMedia(media);
  };

  const isCurrentMedia = (media: TitlePosterOrCoverMediaProps): boolean => {
    return currentMedia !== null && currentMedia.id === media.id && currentMedia.kind === media.kind;
  };

  const handleSave = () => {
    if (!selectedMedia) return;
    setIsLoading(true);

    if (mode === "poster") {
      assetService
        .SetPosterResource(
          titleId,
          selectedMedia.id,
          selectedMedia.kind === "artwork" ? TitlePosterLocation.Artwork : TitlePosterLocation.File
        )
        .then((response) => {
          if (response.success) {
            enqueueSnackbar(t("alert:success.titlePosterOrCoverCard.setPosterSuccess"), "success");
            onSuccess();
            closeModal();
          } else {
            enqueueSnackbar(
              t("alert:error.titlePosterOrCoverCard.setPosterFailed", { message: response.message }),
              "error"
            );
          }
        })
        .finally(() => setIsLoading(false));
    } else if (mode === "cover") {
      assetService
        .SetCoverResource(
          titleId,
          selectedMedia.id,
          selectedMedia.kind === "artwork" ? TitleCoverLocation.Artwork : TitleCoverLocation.File
        )
        .then((response) => {
          if (response.success) {
            enqueueSnackbar(t("alert:success.titlePosterOrCoverCard.setCoverSuccess"), "success");
            onSuccess();
            closeModal();
          } else {
            enqueueSnackbar(
              t("alert:error.titlePosterOrCoverCard.setCoverFailed", { message: response.message }),
              "error"
            );
          }
        })
        .finally(() => setIsLoading(false));
    }
  };

  const onSuccessUpload = (response?: any, temporaryUploadingName?: string) => {
    mediaService.GetFilesByObjectId(Number(titleId)).then(function (response) {
      dispatch(SetFetchedMediaFiles(response.files || []));
    });
    setUploadedFileIds((prev) => [...prev, response.fileId]);
  };

  React.useEffect(() => {
    if (currentMedia) setSelectedMedia(currentMedia);
  }, [currentMedia]);

  return (
    <Modal
      title={
        mode === "poster" ? t("modal:title.titleSetPosterOrCover.poster") : t("modal:title.titleSetPosterOrCover.cover")
      }
      reference={ref}
      closeModal={closeModal}
      width={604}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={closeModal}>{t("common:cancel")}</Button>
          <Button variant="contained" onClick={handleSave} disabled={!selectedMedia || isLoading}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        {format !== undefined ? (
          <>
            <UploadCard
              context={UploadContext.MediaFile}
              format={format}
              objectId={titleId}
              title={t("card:uploadCard.title.uploadNewMedia")}
              userHasRightToDelete={true}
              coverPlaceholderIcon={<UploadFileOutlined />}
              onSuccessUpload={onSuccessUpload}
              uploadButtonInCard
              ratio={mode === "poster" ? "2:3" : "16:9"}
            />
          </>
        ) : (
          <>
            <Alert severity="warning">{t("alert:warning.cannotUploadBecauseMissingFormat")}</Alert>
          </>
        )}

        <Typography variant="body1" mt={3}>
          {t("modal:modalTitleSetPosterOrCover.body.mediaAvailable", { count: mediaCardsData.length })}
        </Typography>
        <Stack mt={2} spacing={1}>
          {mediaCardsData.map((media, index) => (
            <PosterOrCoverMediaCard
              key={index}
              {...media}
              mode={mode}
              isSelected={isMediaSelected(media)}
              isNew={uploadedFileIds.includes(media.id)}
              isCurrent={isCurrentMedia(media)}
              onClick={() => handleSelectMedia(media)}
            />
          ))}
        </Stack>
      </Stack>
    </Modal>
  );
});

export default ModalTitleSetPosterOrCover;
